/*
 * This file contains some common code from:
 * - scenario_explorer_new repository (`src/config.js` and `tests/config.js`)
 * - auth-ui repository (`src/utilities/config.js`)
 * - app-configs repository (`lib/config.mjs`)
 *
 * TODO: reuse code
 */
import {camelCase, flatten, snakeCase, isArray} from 'lodash';
import axios from 'axios';

const {origin, pathname, host, protocol} = window.location;
const defaultConfigLocation = `${origin || `${protocol}//${host}`}${pathname}config.json`;

function parseKey(key) {
  if (/^.*\[\d+]$/.test(key)) {
    return key.match(/^(.*)\[(\d+)]$/).slice(1);
  } else {
    return [key];
  }
}

function containsOnlyNumber(next) {
  return /^\d+$/.test(next);
}

/**
 * Set deep property value
 * @param obj object to set property to
 * @param path property path with separated segments
 * @param value property value
 * @param caseSensitive whether to apply names normalization
 * @param separator property path segments separator, default .
 */
export function setObjectValue(obj, path, value, {caseSensitive = true, separator = '.'} = {}) {
  let [first, ...rest] = path.split(separator);
  const keyParts = parseKey(first);
  // let isArray = false;
  let isArray = !caseSensitive; // for compatibility with old upper-cased configs
  if (keyParts.length > 1) {
    first = keyParts[0];
    rest = [keyParts[1], ...rest];
    isArray = true;
  }
  const key = caseSensitive ? first : camelCase(first);
  if (rest.length === 0) {
    obj[key] = value;
  } else {
    if (!obj.hasOwnProperty(key)) {
      obj[key] = (isArray && containsOnlyNumber(rest[0])) ? [] : {};
    }
    setObjectValue(obj[key], rest.join(separator), value, {caseSensitive, separator});
  }
}

/**
 * Convert key-value pairs list to object
 * @param pairs
 * @return Object
 */
export function fromKeyValuePairs(pairs) {
  const obj = {};
  pairs.forEach(({path, value}) => {
    const caseSensitive = path[0] === path[0].toLowerCase();
    setObjectValue(obj, path, value, {caseSensitive});
  });
  return obj;
}

function getPath(parentPath, key, asArray, caseSensitive, separator) {
  if (asArray && /^\d+$/.test(key)) {
    if (parentPath) {
      return `${parentPath}[${key}]`;
    } else {
      return `[${key}]`;
    }
  } else {
    if (parentPath) {
      if (caseSensitive) {
        return `${parentPath}${separator}${key}`;
      } else {
        return `${parentPath}${separator}${snakeCase(key).toUpperCase()}`;
      }
    } else {
      if (caseSensitive) {
        return key;
      } else {
        return snakeCase(key).toUpperCase();
      }
    }
  }
}

function convertValue(parentPath, key, value, {asArray = false, caseSensitive = true, separator = '.'} = {}) {
  const path = getPath(parentPath, key, asArray, caseSensitive, separator);
  switch (typeof value) {
    case 'boolean':
      return [{path, type: 'BOOLEAN', value}];
    case 'number':
      return [{path, type: 'INTEGER', value}];
    case 'string':
      return [{path, type: 'STRING', value}];
    case 'object':
      if (isArray(value)) {
        return flatten(value.map((v, i) => convertValue(path, `${i}`, v, {asArray: true, caseSensitive, separator})));
      } else {
        return flatten(Object.entries(value).map(([k, v]) => convertValue(path, k, v, {caseSensitive, separator})));
      }
    default:
      return [];
  }
}

/**
 * Convert object to key-value pairs list
 * @param obj
 * @returns Array
 */
export function toKeyValuePairs(obj) {
  return convertValue(null, null, obj);
}

/**
 * Fetch config as json
 * @param configLocation URL
 * @return {Promise<Object>}
 */
export async function getConfig(configLocation = defaultConfigLocation) {
  try {
    const response = await axios.get(configLocation);
    let {data: config} = response;
    if (!(config.records && config.records.length) && !(isArray(config) && config.length) && !config.authUrl) {
      console.error(`The config file ${configLocation} is invalid`);
      return undefined;
    }
    // check whether response is full server config
    if (config.records) {
      config = fromKeyValuePairs(config.records);
    }
    // new API endpoint returning just array of records
    if (isArray(config)) {
      config = fromKeyValuePairs(config);
    }
    return config;
  } catch (e) {
    if (e.response && e.response.status === 404) {
      return undefined;
    }
    throw e;
  }
}
